import React, { useContext, useEffect, useMemo, useState } from 'react'
import Select from 'react-select'
import Typography from '../../../../Typography'
import { currencyFormatter } from '../../../../../utils/functions'
import Button from '../../../../Button'
import styles from './YearbookForm.module.sass'
import useBreakpoints from '../../../../../hooks/use-breakpoints'
import { Cart, OrderPackage } from '../../../../../models'
import TextInput from '../../../../TextInput'
import { useHistory } from 'react-router-dom'
import { CustomerContext } from '../../../../../contexts/CustomerContext'
import routes from '../../../../../routes';

function getInitialEmail(student, customer) {
  if (student.email) return student.email;
  if (customer && customer.email) return customer.email;

  return '';
}

function hasInitialEmail(student, customer) {
  if (student.email || (customer && customer.email)) return true;

  return false;
}

const YearbookForm = ({
  student, school, defaultYearbook, yearbooks = [], nextRoute = routes.review(), pushToNextRoute = true, afterAdd = () => { }, buttonText = 'Proceed to Checkout'
}) => {
  const [yearbook, setYearbook] = useState(defaultYearbook)
  const breakpoint = useBreakpoints()
  const history = useHistory()
  const { customer, setCustomer } = useContext(CustomerContext)

  const initialEmail = getInitialEmail(student, customer);
  const hasInitialEmailSet = hasInitialEmail(student, customer);
  const [email, setEmail] = useState(initialEmail);

  useEffect(() => {
    if (initialEmail === '') return;
    if (!hasInitialEmailSet) return;

    setEmail(initialEmail);
  }, [initialEmail]);

  const [includeName, setIncludeName] = useState(false);
  const [includePersonalization, setIncludePersonalization] = useState(false);

  const [includeDigital, setIncludeDigital] = useState(false);
  const [displayDigital, setDisplayDigital] = useState(false);

  useEffect(() => {
    setIncludePersonalization(school.id != 101 && school.id != 15 && school.id != 104 && school.id != 1088 );
  }, [yearbook]);

  useEffect(() => {
    setIncludeName(includePersonalization);
  }, [includePersonalization]);

  useEffect(() => {
    setDisplayDigital(false);
  }, [yearbook]);

  useEffect(() => {
    setIncludeDigital(displayDigital);
  }, [displayDigital]);

  const pricePerYearbook = useMemo(() => {
    if (!yearbook) return 0

    const prices = yearbook.options.map((option) => option.prices).flat()

    const schoolPrice = prices.find((price) => price.school)

    if (schoolPrice) return schoolPrice.price

    const genericPrice = prices.find((price) => price.price)

    return genericPrice.price
  }, [yearbook && yearbook.id])

  const myCart = async () => {
    if (customer && customer.cart) {
      customer.cart.school = school

      const initializedCart = new Cart(customer.cart);

      initializedCart.isPersisted = true;

      await initializedCart.save();

      initializedCart.email = email;

      await initializedCart.save();

      return initializedCart;
    }

    return new Cart({ email, schoolId: student.schoolId });
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();

    const cart = await myCart();

    const orderPackages = Array.from(Array(parseInt(1, 10)).keys());

    cart.orderPackages = orderPackages.map(() => new OrderPackage({
      optionId: yearbook.options && yearbook.options.find(op => op.prices.length > 0).id,
      price: pricePerYearbook,
      packageId: yearbook.id,
      hasNameOnCover: includeName,
      hasDigitalYearbook: includeDigital,
      studentId: student.id,
    }));

    cart
      .save({ with: 'orderPackages' })
      .then(() => {
        setCustomer({
          ...customer, email, priorityCart: cart, currentStudent: student, currentSchool: school,
        });
        if (pushToNextRoute) history.push(nextRoute);
        afterAdd();
      });
  };

  useEffect(() => {
    if (breakpoint === 'sm') {
      document.body.style.background = '#efefef'
    } else {
      document.body.style.background = '#fff'
    }
  }, [breakpoint])

  if (!yearbook) return null

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div>
        <label>
          <Typography weight="bold">{student.fullName(true)}</Typography>
        </label>
      </div>
      <div>
        <label>
          <Typography weight="bold">{school.name}</Typography>
        </label>
      </div>
      <div>
        <label style={{ display: 'flex', gap: '.5rem' }}>
          <Typography>Price per yearbook:</Typography>
          <Typography weight="bold">{currencyFormatter(pricePerYearbook)}</Typography>
        </label>
      </div>
      <div>
        {!hasInitialEmailSet && (
          <label className={styles.subrow}>
            <Typography>Email:</Typography>
            <TextInput required type="email" val={email} onChange={(evt) => setEmail(evt.target.value)} />
          </label>
        )}
        {yearbooks.length > 1 && (
          <label className={styles.subrow}>
            <Typography>Select Yearbook</Typography>
            <Select
              value={{ label: yearbook.name, value: yearbook.id }}
              onChange={(opt) => setYearbook(yearbooks.find((yearbookPackage) => yearbookPackage.id === opt.value))}
              options={yearbooks.map((yearbookPackage) => ({
                label: yearbookPackage.name,
                value: yearbookPackage.id,
              }))}
            />
          </label>
        )}
        {includePersonalization && (
          <div className={styles.box}>
            <span>Customization (Optional):</span>
            <label>
              <input type="checkbox" checked={includeName} value={includeName} onChange={() => setIncludeName((prev) => !prev)} />
              <span>Name & Photo on Cover <span className={styles.namePricing}>($5.00)</span></span>
            </label>
          </div>
        )}
        {displayDigital && (
          <div className={styles.box}>
            <span>Add-On Digital Yearbook (Optional):</span>
            <label>
              <input
                type="checkbox"
                checked={includeDigital}
                value="true"
                onChange={() => setIncludeDigital((prev) => !prev)}
              />
              <span>Digital Yearbook Bonus File <span className={styles.namePricing}>($5.00)</span></span>
            </label>
          </div>
        )}
        <label style={{ display: 'flex', justifyContent: 'flex-end', margin: '1.5rem 0' }}>
          <Typography>Subtotal:</Typography>
          <Typography weight="bold">
            {currencyFormatter(includeName || includeDigital ? pricePerYearbook + 5 : pricePerYearbook)}
          </Typography>
        </label>
      </div>
      <Button fullWidth variant="primary" circular type="submit">{buttonText}</Button>
    </form>
  )
}

export default YearbookForm
